/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * Form Field Validation Component
 * Provides real-time validation feedback for form fields
 */
export const FormFieldValidation = ({ 
  fieldName, 
  value, 
  touched, 
  errors, 
  validationRules = {},
  showIcon = true 
}) => {
  const error = errors[fieldName];
  const isValid = touched[fieldName] && !error && value;
  const showError = touched[fieldName] && error;

  if (!touched[fieldName] && !error) {
    return null;
  }

  return (
    <div style={{ marginTop: '4px', minHeight: '20px' }}>
      {showError && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#ef4444',
          fontSize: '12px',
          marginTop: '4px'
        }}>
          {showIcon && <FontAwesomeIcon icon={faExclamationCircle} style={{ fontSize: '12px' }} />}
          <span>{error}</span>
        </div>
      )}
      {isValid && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#10b981',
          fontSize: '12px',
          marginTop: '4px'
        }}>
          {showIcon && <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '12px' }} />}
          <span>Looks good!</span>
        </div>
      )}
    </div>
  );
};

/**
 * Validation Helper Functions
 */
export const validateEmail = (email) => {
  if (!email) return 'Email is required';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  return null;
};

export const validatePhone = (phone) => {
  if (!phone) return 'Phone number is required';
  const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
  if (!phoneRegex.test(phone.replace(/\s/g, ''))) return 'Please enter a valid phone number';
  return null;
};

export const validateURL = (url) => {
  if (!url) return 'Website URL is required';
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL (e.g., https://example.com)';
  }
};

export const validateRequired = (value, fieldName) => {
  if (!value || value.toString().trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateMinLength = (value, minLength, fieldName) => {
  if (value && value.toString().length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  return null;
};

export const validateMaxLength = (value, maxLength, fieldName) => {
  if (value && value.toString().length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters`;
  }
  return null;
};

export const validateYear = (year) => {
  if (!year) return 'Year is required';
  const currentYear = new Date().getFullYear();
  const yearNum = parseInt(year);
  if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
    return `Year must be between 1900 and ${currentYear}`;
  }
  return null;
};

export const validateNumber = (value, fieldName, min = null, max = null) => {
  if (!value) return `${fieldName} is required`;
  const num = parseInt(value);
  if (isNaN(num)) return `${fieldName} must be a valid number`;
  if (min !== null && num < min) return `${fieldName} must be at least ${min}`;
  if (max !== null && num > max) return `${fieldName} must be no more than ${max}`;
  return null;
};

/**
 * Hook for form validation
 */
export const useFormValidation = (initialValues = {}) => {
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});

  const validateField = (name, value, rules = {}) => {
    const fieldErrors = [];
    
    // Required validation
    if (rules.required && (!value || value.toString().trim() === '')) {
      fieldErrors.push(rules.requiredMessage || `${name} is required`);
    }
    
    // Email validation
    if (rules.email && value) {
      const emailError = validateEmail(value);
      if (emailError) fieldErrors.push(emailError);
    }
    
    // Phone validation
    if (rules.phone && value) {
      const phoneError = validatePhone(value);
      if (phoneError) fieldErrors.push(phoneError);
    }
    
    // URL validation
    if (rules.url && value) {
      const urlError = validateURL(value);
      if (urlError) fieldErrors.push(urlError);
    }
    
    // Min length validation
    if (rules.minLength && value) {
      const minError = validateMinLength(value, rules.minLength, name);
      if (minError) fieldErrors.push(minError);
    }
    
    // Max length validation
    if (rules.maxLength && value) {
      const maxError = validateMaxLength(value, rules.maxLength, name);
      if (maxError) fieldErrors.push(maxError);
    }
    
    // Year validation
    if (rules.year && value) {
      const yearError = validateYear(value);
      if (yearError) fieldErrors.push(yearError);
    }
    
    // Number validation
    if (rules.number && value) {
      const numError = validateNumber(value, name, rules.min, rules.max);
      if (numError) fieldErrors.push(numError);
    }
    
    // Custom validation
    if (rules.custom && value) {
      const customError = rules.custom(value);
      if (customError) fieldErrors.push(customError);
    }
    
    return fieldErrors.length > 0 ? fieldErrors[0] : null;
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleChange = (name, value, rules = {}) => {
    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, value, rules);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const validateForm = (values, rules) => {
    const newErrors = {};
    Object.keys(rules).forEach(fieldName => {
      const error = validateField(fieldName, values[fieldName], rules[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });
    setErrors(newErrors);
    setTouched(Object.keys(rules).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    return Object.keys(newErrors).length === 0;
  };

  const reset = () => {
    setErrors({});
    setTouched({});
  };

  return {
    errors,
    touched,
    handleBlur,
    handleChange,
    validateField,
    validateForm,
    reset,
    setTouched
  };
};

export default FormFieldValidation;
